import { Component, Input, ViewEncapsulation, inject } from '@angular/core';
import { SmartCartPreviewStateService } from '../../smart-cart-preview-state.service';
import {
  getActiveTimerPreviewState,
  getSampleTimerCountdown,
  getTimerPreviewStyle,
} from '../../../components/timer-config/timer.model';
import {
  GLOBAL_BUTTON_DESIGN_DEFAULT,
  GlobalButtonDesign,
  GoalApplySectionPreview,
  GoalPreviewSetting,
  GoalsPadding,
  GoalsProgressBarSettings,
  TimerStatePreview,
} from '../widget-preview.types';

const PADDING_MAP: Record<GoalsPadding, string> = { none: '0', small: '8px', medium: '16px', large: '24px' };
const NOT_MET_FILL = 60;
const SAMPLE_AMOUNT_LEFT = 5.99;
const SAMPLE_ITEMS_LEFT = 2;
const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$' };
// Must match .checkpoint's rendered diameter in the SCSS, and LABEL_GAP in SingleBar.tsx.
const CHECKPOINT_SIZE = 34;
const LABEL_GAP = 6;

interface PreviewCheckpoint {
  originalIndex: number;
  iconUrl: string;
  iconAbove: boolean;
  aboveText: string;
  belowText: string;
  labelSize: number;
  inlineFill: number;
  position: number;
  completed: boolean;
}

@Component({
  selector: 'goals-progress-bar-preview',
  templateUrl: './goals-progress-bar-preview.component.html',
  styleUrls: ['./goals-progress-bar-preview.component.scss'],
  // None so our CSS can reset the <p> margins inside the rich-text injected via [innerHTML].
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GoalsProgressBarPreviewComponent {
  @Input() settings!: GoalsProgressBarSettings;
  @Input() globalButtonDesign: GlobalButtonDesign = GLOBAL_BUTTON_DESIGN_DEFAULT;

  private readonly previewState = inject(SmartCartPreviewStateService);

  get boxStyle(): Record<string, string> {
    const bg = this.settings.appearance.background;
    return {
      'background-color': bg.color,
      padding: PADDING_MAP[bg.padding] ?? '8px',
      'border-radius': `${bg.cornerRadius || 0}px`,
      border: bg.border.enabled ? `${bg.border.width}px solid ${bg.border.color}` : 'none',
    };
  }

  // On the outer wrapper, not the inner box, so the rule spans the full drawer width like the Top Bar's.
  get outerStyle(): Record<string, string> {
    const border = this.settings.appearance.bottomBorder;
    return { 'border-bottom': border?.enabled ? `1px solid ${border.color}` : 'none' };
  }

  get barType(): string {
    return this.settings.appearance.barType;
  }

  get fillColor(): string {
    return this.settings.appearance.progressBar.color || '#000000';
  }

  get barHeight(): number {
    return this.settings.appearance.progressBar.barWidth || 8;
  }

  // How far the checkpoint circle sticks out past the bar on each side (see .checkpoint in the SCSS).
  get checkpointOverhang(): number {
    return Math.max(0, (CHECKPOINT_SIZE - this.barHeight) / 2);
  }

  // The last checkpoint is centred on the bar's 100% mark, so the bar stops half a circle short of the
  // content edge — otherwise the marker hangs past it and the widget stops lining up with the cart items
  // and checkout below. Mirrors BAR_EDGE_RESERVE in SingleBar.tsx.
  get barEdgeReserve(): number {
    return CHECKPOINT_SIZE / 2;
  }

  // Columns 2 and 3 are reserve-only: the bar and label rows span 1-2, so the bar ends at the 2|3
  // boundary, and a hidden twin of the end label spans 2-4. Grid splits a spanning item's width equally
  // between two auto tracks, so each reserves half the label — exactly the room the centred end label
  // needs on the right, with no measuring. Mirrors SingleBar.tsx.
  get barGridColumns(): string {
    return `minmax(0, 1fr) minmax(${this.barEdgeReserve}px, auto) minmax(${this.barEdgeReserve}px, auto)`;
  }

  // The labels on the last checkpoint — the ones that drive the reserve above.
  get endLabelTexts(): string[] {
    const end = this.checkpoints[this.checkpoints.length - 1];
    return end ? [end.aboveText, end.belowText].filter(Boolean) : [];
  }

  get endLabelSize(): number {
    return this.checkpoints[this.checkpoints.length - 1]?.labelSize ?? 12;
  }

  // Single-bar labels are offset from the circle, not the bar, so the label above and the label below
  // land the same distance from the marker whatever the bar height is. Mirrors SingleBar.tsx.
  get checkpointLabelOffset(): number {
    return this.checkpointOverhang + LABEL_GAP;
  }

  get labelLayerHeight(): number {
    return Math.max(...this.checkpoints.map((cp) => cp.labelSize), 0);
  }

  get singleHasAboveLabels(): boolean {
    return this.checkpoints.some((cp) => !!cp.aboveText);
  }

  get singleHasBelowLabels(): boolean {
    return this.checkpoints.some((cp) => !!cp.belowText);
  }

  get barRadius(): number {
    const cr = this.settings.appearance.progressBar.cornerRadius;
    return cr > 0 ? cr : this.barHeight / 2;
  }

  // Read off the same goal that supplies the message text, so spacing and words never disagree.
  // Vertical only, and 'medium' when unset — mirrors the storefront exactly.
  get messagePadding(): string {
    return `${PADDING_MAP[this.activeGoal?.appearance?.messagePadding ?? 'medium']} 0`;
  }

  get messageHtml(): string {
    const goal = this.activeGoal;
    if (!goal) return '';
    const raw = this.met ? goal.messageWhenMet : goal.messageWhenNotMet;
    return this.substituteTokens(raw ?? '', goal);
  }

  get timerEnabled(): boolean {
    return !!this.settings.timer?.enabled;
  }

  get timerPlacement(): string {
    return this.settings.timer?.placement ?? 'inline';
  }

  get showTimerStrip(): boolean {
    return this.timerEnabled && this.timerPlacement === 'inline';
  }

  get showTimerBadge(): boolean {
    return this.timerEnabled && this.timerPlacement === 'badge';
  }

  get timerMessageHtml(): string {
    const message = this.activeTimerState?.message ?? '';
    return message.replace(/\{countdown\}/g, this.sampleCountdown);
  }

  get timerAlign(): string {
    const html = this.timerMessageHtml;
    if (html.includes('ql-align-center')) return 'center';
    if (html.includes('ql-align-right')) return 'flex-end';
    return 'flex-start';
  }

  get timerIconUrl(): string {
    const icon = this.activeTimerState?.iconUrl;
    return typeof icon === 'string' ? icon : '';
  }

  get timerStyle(): Record<string, string> {
    return getTimerPreviewStyle(this.activeTimerState);
  }

  private get activeTimerState(): TimerStatePreview | undefined {
    return getActiveTimerPreviewState(this.settings.timer, this.previewState.goalsPreviewTimerState());
  }

  private get sampleCountdown(): string {
    return getSampleTimerCountdown(this.previewState.goalsPreviewTimerState(), this.settings.timer?.format);
  }

  readonly trackColor = '#e1e3e5';

  get checkpoints(): PreviewCheckpoint[] {
    const n = this.enabledGoals.length;
    const active = this.previewedPos;
    return this.enabledGoals.map(({ goal, originalIndex }, pos) => ({
      originalIndex,
      iconUrl: typeof goal.appearance.iconUrl === 'string' ? goal.appearance.iconUrl : '',
      iconAbove: goal.appearance.firstLabelPosition !== 'below_bar',
      aboveText: this.sideText(goal, 'above_bar'),
      belowText: this.sideText(goal, 'below_bar'),
      labelSize: goal.appearance.labelSize || 12,
      inlineFill: this.inlineFill(pos),
      position: n ? ((pos + 1) / n) * 100 : 0,
      completed: pos < active || (pos === active && this.met),
    }));
  }

  get activeCheckpoint(): PreviewCheckpoint | null {
    const pos = this.previewedPos;
    return pos >= 0 ? (this.checkpoints[pos] ?? null) : null;
  }

  get inlineHasAbove(): boolean {
    return this.checkpoints.some((cp) => !!cp.aboveText || (!!cp.iconUrl && cp.iconAbove));
  }

  get inlineHasBelow(): boolean {
    return this.checkpoints.some((cp) => !!cp.belowText || (!!cp.iconUrl && !cp.iconAbove));
  }

  get singleFillPercent(): number {
    const n = this.enabledGoals.length;
    if (!n) return 0;
    const checkpoint = ((this.previewedPos + 1) / n) * 100;
    return this.met ? checkpoint : Math.max(0, checkpoint - (100 / n) * 0.5);
  }

  get sequentialFillPercent(): number {
    return this.met ? 100 : NOT_MET_FILL;
  }

  get completedPills(): string[] {
    if (!this.settings.appearance.pills.enabled) return [];
    const active = this.previewedPos;
    if (active < 0) return [];
    const completedCount = this.met ? active + 1 : active;
    return this.enabledGoals.slice(0, completedCount).map(({ goal }) => this.pillLabel(goal));
  }

  get pillStyle(): Record<string, string> {
    const pills = this.settings.appearance.pills;
    return {
      'background-color': pills.backgroundColor,
      color: pills.textColor,
      'border-radius': `${pills.cornerRadius || 0}px`,
    };
  }

  get showApplySection(): boolean {
    const goal = this.activeGoal;
    return this.met && !!goal && goal.offer?.type === 'product' && (goal.offer.product?.products?.length ?? 0) > 0;
  }

  get applySection(): GoalApplySectionPreview | null {
    return this.activeGoal?.appearance.applySection ?? null;
  }

  get isMultipleGifts(): boolean {
    return this.activeGoal?.offer.product.giftMode === 'multiple';
  }

  get productDisplayType(): string {
    return this.activeGoal?.offer.product.productDisplayType ?? 'list';
  }

  get applyProductsView(): { name: string; imageUrl?: string; price: string }[] {
    const products = this.activeGoal?.offer.product.products ?? [];
    return products.map((product) => ({
      name: product.name,
      imageUrl: product.imageUrl,
      price: product.price ? (/^[0-9]/.test(product.price) ? `$${product.price}` : product.price) : '',
    }));
  }

  get applyBoxStyle(): Record<string, string> {
    const bg = this.applySection?.background;
    if (!bg) return {};
    return {
      'background-color': bg.color,
      padding: PADDING_MAP[bg.padding] ?? '8px',
      'border-radius': `${bg.cornerRadius || 0}px`,
      border: bg.border.enabled ? `${bg.border.width}px solid ${bg.border.color}` : 'none',
    };
  }

  get applyTextStyle(): Record<string, string> {
    const texts = this.applySection?.texts;
    return { color: texts?.textColor ?? '#000000', 'font-size': `${texts?.fontSize ?? 14}px` };
  }

  get applyButtonStyle(): Record<string, string> {
    const button = this.applySection?.button;
    return {
      'background-color': button?.backgroundColor ?? this.globalButtonDesign.backgroundColor,
      color: button?.textColor ?? this.globalButtonDesign.textColor,
      'border-radius': `${button?.cornerRadius ?? this.globalButtonDesign.cornerRadius}px`,
    };
  }

  get applyImageStyle(): Record<string, string> {
    const image = this.applySection?.image;
    return {
      padding: `${image?.innerPadding ?? 0}px`,
      border: image?.borderWidth ? `${image.borderWidth}px solid #e1e3e5` : '1px solid #e1e3e5',
      'border-radius': `${image?.cornerRadius ?? 8}px`,
    };
  }

  get applyImageObjectFit(): string {
    return this.applySection?.image?.fit === 'fill' ? 'fill' : 'cover';
  }

  private get met(): boolean {
    return this.previewState.goalsPreviewMet();
  }

  private get enabledGoals(): { goal: GoalPreviewSetting; originalIndex: number }[] {
    return (this.settings?.goals ?? [])
      .map((goal, originalIndex) => ({ goal, originalIndex }))
      .filter(({ goal }) => goal.enabled);
  }

  private get previewedPos(): number {
    const enabled = this.enabledGoals;
    if (!enabled.length) return -1;
    const pos = enabled.findIndex((g) => g.originalIndex === this.previewState.goalsPreviewGoalIndex());
    return pos === -1 ? 0 : pos;
  }

  private get activeGoal(): GoalPreviewSetting | null {
    const pos = this.previewedPos;
    return pos >= 0 ? this.enabledGoals[pos].goal : null;
  }

  private inlineFill(pos: number): number {
    const active = this.previewedPos;
    if (pos < active) return 100;
    if (pos === active) return this.met ? 100 : NOT_MET_FILL;
    return 0;
  }

  private sideText(goal: GoalPreviewSetting, side: 'above_bar' | 'below_bar'): string {
    if (!goal.appearance.showLabelsInProgressBar) return '';
    const parts: string[] = [];
    if (goal.appearance.firstLabelPosition === side) parts.push(this.labelText(goal, 'first'));
    if (goal.appearance.secondLabelPosition === side) parts.push(this.labelText(goal, 'second'));
    return parts.filter(Boolean).join(' · ');
  }

  private labelText(goal: GoalPreviewSetting, which: 'first' | 'second'): string {
    const raw = which === 'first' ? goal.appearance.firstLabel : goal.appearance.secondLabel;
    if (!raw) return '';
    return raw.replace(/\{threshold\}/g, this.formatThreshold(goal));
  }

  private pillLabel(goal: GoalPreviewSetting): string {
    if (goal.appearance.pillText) return goal.appearance.pillText.replace(/\{threshold\}/g, this.formatThreshold(goal));
    return this.labelText(goal, goal.appearance.pillSource) || this.formatThreshold(goal);
  }

  private substituteTokens(text: string, goal: GoalPreviewSetting): string {
    const configuredThreshold = goal.thresholds?.[0]?.threshold;
    const threshold = configuredThreshold ?? SAMPLE_AMOUNT_LEFT;
    const currency = goal.thresholds?.[0]?.currency;
    let result = (text ?? '')
      .replace(/\{amount-left\}/g, this.formatMoney(threshold, currency))
      .replace(/\{items-left\}/g, String(configuredThreshold ?? SAMPLE_ITEMS_LEFT))
      .replace(/\{threshold\}/g, this.formatThreshold(goal));
    if (this.timerEnabled && this.timerPlacement === 'in_goal_message') {
      result = result.replace(/\{countdown\}/g, this.sampleCountdown);
    }
    return result;
  }

  private formatThreshold(goal: GoalPreviewSetting): string {
    const threshold = goal.thresholds?.[0];
    if (!threshold) return '';
    return this.settings.minimumThresholdType === 'cart_value'
      ? this.formatMoney(threshold.threshold, threshold.currency)
      : `${threshold.threshold} items`;
  }

  private formatMoney(value: number, currency: string | undefined): string {
    const symbol = CURRENCY_SYMBOLS[currency ?? ''] ?? '$';
    return `${symbol}${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}`;
  }
}
